import React from 'react'

interface StorkDeliveryProps {
  size?: number
  className?: string
  animated?: boolean
  gender?: 'boy' | 'girl'
}

const StorkDelivery: React.FC<StorkDeliveryProps> = ({ 
  size = 120, 
  className = '', 
  animated = true,
  gender = 'boy'
}) => {
  const bundleColor = gender === 'boy' ? '#3b82f6' : '#ec4899'
  const accentColor = gender === 'boy' ? '#60a5fa' : '#f9a8d4'

  return (
    <div className={className} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="storkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f8fafc" />
            <stop offset="50%" stopColor="#e2e8f0" />
            <stop offset="100%" stopColor="#cbd5e1" />
          </linearGradient>
          
          <linearGradient id="bundleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={bundleColor} />
            <stop offset="100%" stopColor={accentColor} />
          </linearGradient>

          <filter id="cloudShadow">
            <feDropShadow dx="2" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.1)"/>
          </filter>
        </defs>

        {/* Background Clouds */}
        <g opacity="0.6" filter="url(#cloudShadow)">
          <ellipse cx="20" cy="30" rx="12" ry="6" fill="#e2e8f0" />
          <ellipse cx="25" cy="28" rx="8" ry="4" fill="#f1f5f9" />
          <ellipse cx="90" cy="25" rx="15" ry="8" fill="#e2e8f0" />
          <ellipse cx="95" cy="22" rx="10" ry="5" fill="#f1f5f9" />
          <ellipse cx="70" cy="90" rx="10" ry="5" fill="#e2e8f0" />
          <ellipse cx="15" cy="95" rx="8" ry="4" fill="#f1f5f9" />
          
          {animated && (
            <animateTransform
              attributeName="transform"
              type="translate"
              values="0,0;10,0;0,0"
              dur="15s"
              repeatCount="indefinite"
            />
          )}
        </g>

        {/* Stork Body */}
        <g className="stork">
          {/* Body */}
          <ellipse cx="60" cy="45" rx="18" ry="12" fill="url(#storkGradient)" />
          
          {/* Neck */}
          <ellipse cx="75" cy="35" rx="4" ry="15" fill="url(#storkGradient)" />
          
          {/* Head */}
          <circle cx="78" cy="25" r="6" fill="url(#storkGradient)" />
          
          {/* Beak */}
          <path d="M 83 25 L 95 27 L 90 29 Z" fill="#f59e0b" />
          
          {/* Eye */}
          <circle cx="76" cy="23" r="1.5" fill="#1f2937" />
          
          {/* Wing */}
          <path 
            d="M 50 40 Q 40 35 45 50 Q 55 55 60 45" 
            fill="#cbd5e1" 
            stroke="#94a3b8" 
            strokeWidth="1"
          >
            {animated && (
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 50 40;-15 50 40;0 50 40"
                dur="2s"
                repeatCount="indefinite"
              />
            )}
          </path>
          
          {/* Legs */}
          <line x1="55" y1="55" x2="52" y2="70" stroke="#f59e0b" strokeWidth="2" />
          <line x1="65" y1="55" x2="68" y2="70" stroke="#f59e0b" strokeWidth="2" />
          
          {/* Feet */}
          <path d="M 50 70 L 48 72 M 52 70 L 54 72 M 50 70 L 52 72" stroke="#f59e0b" strokeWidth="1" />
          <path d="M 66 70 L 64 72 M 68 70 L 70 72 M 66 70 L 68 72" stroke="#f59e0b" strokeWidth="1" />

          {/* Flying Animation */}
          {animated && (
            <animateTransform
              attributeName="transform"
              type="translate"
              values="0,0;5,-2;0,0"
              dur="3s"
              repeatCount="indefinite"
            />
          )}
        </g>

        {/* Baby Bundle */}
        <g className="bundle">
          {/* Bundle Cloth */}
          <ellipse cx="85" cy="50" rx="12" ry="8" fill="url(#bundleGradient)" />
          
          {/* Bundle Tie */}
          <ellipse cx="85" cy="45" rx="8" ry="3" fill={bundleColor} />
          <circle cx="85" cy="45" r="2" fill="#fbbf24" />
          
          {/* Baby Face Silhouette */}
          <circle cx="85" cy="52" r="3" fill="rgba(255,255,255,0.8)" />
          <circle cx="83" cy="50" r="0.5" fill="#1f2937" />
          <circle cx="87" cy="50" r="0.5" fill="#1f2937" />
          <path d="M 83 53 Q 85 54 87 53" stroke="#1f2937" strokeWidth="0.5" fill="none" />
          
          {/* Bundle Pattern */}
          <g opacity="0.6">
            <circle cx="78" cy="48" r="1" fill="rgba(255,255,255,0.5)" />
            <circle cx="92" cy="52" r="1" fill="rgba(255,255,255,0.5)" />
            <circle cx="82" cy="55" r="0.8" fill="rgba(255,255,255,0.4)" />
            <circle cx="88" cy="47" r="0.8" fill="rgba(255,255,255,0.4)" />
          </g>

          {/* Swaying Animation */}
          {animated && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 85 50;5 85 50;-5 85 50;0 85 50"
              dur="4s"
              repeatCount="indefinite"
            />
          )}
        </g>

        {/* Sparkles Around Bundle */}
        {animated && (
          <g className="sparkles">
            <g>
              <polygon points="95,35 96,37 98,36 96,38" fill="#ffd700">
                <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" begin="0s" />
              </polygon>
            </g>
            
            <g>
              <polygon points="72,55 73,57 75,56 73,58" fill="#fff">
                <animate attributeName="opacity" values="0;1;0" dur="1.8s" repeatCount="indefinite" begin="0.5s" />
              </polygon>
            </g>
            
            <g>
              <polygon points="90,65 91,67 93,66 91,68" fill={bundleColor}>
                <animate attributeName="opacity" values="0;1;0" dur="2.2s" repeatCount="indefinite" begin="1s" />
              </polygon>
            </g>
            
            <g>
              <polygon points="98,45 99,47 101,46 99,48" fill="#ff69b4">
                <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" begin="1.5s" />
              </polygon>
            </g>
          </g>
        )}

        {/* Hearts */}
        <g className="hearts" opacity="0.7">
          <path d="M 25,60 C 25,57 27,55 30,55 C 33,55 35,57 35,60 C 35,63 30,68 30,68 C 30,68 25,63 25,60 Z" fill="#ff69b4">
            {animated && (
              <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" />
            )}
          </path>
          
          <path d="M 100,75 C 100,73 101,72 103,72 C 105,72 106,73 106,75 C 106,77 103,80 103,80 C 103,80 100,77 100,75 Z" fill="#ff69b4">
            {animated && (
              <animate attributeName="opacity" values="0.5;1;0.5" dur="2.5s" repeatCount="indefinite" />
            )}
          </path>
        </g>

        {/* Delivery Route Trail */}
        {animated && (
          <path 
            d="M 10 80 Q 40 70 60 45 Q 80 30 110 20" 
            stroke={bundleColor} 
            strokeWidth="2" 
            fill="none" 
            opacity="0.3"
            strokeDasharray="5,5"
          >
            <animate 
              attributeName="stroke-dasharray" 
              values="5,5;10,0;5,5" 
              dur="5s" 
              repeatCount="indefinite" 
            />
          </path>
        )}
      </svg>
    </div>
  )
}

export default StorkDelivery
